import React, {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {DialogHeader, StyledCustomDialog, StyledDialogContent} from './styled';
import {IconButton, CloseIcon, Divider, Text} from '@phonectas/unifon-ui-kit';
import NewInfoIcon from '../icons/NewInfoIcon';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {ThreeSixtyStoreState} from 'common/types/store/threeSixtyTypes';

export interface DialogAction {
	label: string;
	onClick: () => void;
	primary?: string;
	color?: string;
}

interface UpsellDialogProps {
    header: string;
    onClose: () => void;
    children?: React.ReactNode;
    show: boolean;
    width?: number;
	noOverflow?: boolean;
	maxHeight?: number;
	noPadding?: boolean;
	note?: boolean;
}

const UpsellDialog: React.FC<UpsellDialogProps> = ({
	header,
	children,
	onClose,
	show,
	width,
	maxHeight,
	noOverflow = false,
	noPadding = false,
}) => {
	const [t] = useTranslation();
	const {
		data
	} = useThreeSixtyNewStore((state: ThreeSixtyStoreState) => ({
		data: state?.upsellDetails,
	}), shallow);
	const hasSoldSubscriptions = data?.has_sold_subscription || false;
	const hasSoldProducts = data?.has_sold_upsell || false;
	const onKeyDown = useCallback(({key}: KeyboardEvent) => {
		if (key === 'Escape') onClose();
	}, [onClose]);

	useEffect(() => {
		window.addEventListener('keydown', onKeyDown);

		return () => {
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [onKeyDown]);

	return (
		<StyledCustomDialog
			{...(width !== undefined ? {$width: width} : {})}
			show={show}
			onClose={onClose}
			$noOverflow={noOverflow}
			$noPadding={noPadding}
			{...maxHeight !== undefined ? {$maxHeight: maxHeight} : {}}
		>
			<StyledDialogContent>
				<DialogHeader>
					<Text variant="h1">{header}</Text>
					<IconButton onClick={onClose} aria-label={t('Close dialog')}>
						<CloseIcon />
					</IconButton>
				</DialogHeader>
				{
					hasSoldSubscriptions || hasSoldProducts ? (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '5px',
								padding: '0 16px 16px 16px',
							}}
						>
							<NewInfoIcon />
							<Text color="secondary">
								{t('Previously ordered an upselling')}
							</Text>
							<Text color="secondary">
								{`( ${hasSoldProducts ? t('Product') : ''}${
									hasSoldSubscriptions
										? (hasSoldProducts ? ' & ' : '') + t('Subscription')
										: ''
								} )`}
							</Text>
						</div>
					) : null
				}
				<Divider />
				{children}
			</StyledDialogContent>
		</StyledCustomDialog>
	);
}

export default UpsellDialog;